import React, { useState, useRef, useEffect } from "react";
import { Typography } from "@/components/ui/typography";
import ArrowLightMode from "@/assets/svg/arrow_light_mode.svg";
import ArrowDarkMode from "@/assets/svg/arrow_dark_mode.svg";

interface DropdownOption {
  label: string;
  value: string;
}

interface DialogDropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: {
    days: string;
    startDate: string;
    endDate: string;
  }) => void;
  placeholder?: string;
  icon?: string;
  className?: string;
}

const formatDate = (date: Date) => {
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

const getDateRange = (daysSelected: string) => {
  const days = Number(daysSelected) || 1;

  const today = new Date();
  const endDate = formatDate(today);

  const start = new Date();
  start.setDate(start.getDate() - days);
  const startDate = formatDate(start);

  return { startDate, endDate };
};

const DialogDropdown: React.FC<DialogDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select option",
  icon,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const selectedLabel =
    options.find((opt) => opt.value === value)?.label || placeholder;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Reset focused index when dropdown opens
  useEffect(() => {
    if (isOpen) {
      const currentIndex = options.findIndex((opt) => opt.value === value);
      setFocusedIndex(currentIndex >= 0 ? currentIndex : 0);
    } else {
      setFocusedIndex(-1);
    }
  }, [isOpen, value, options]);

  // Scroll focused option into view
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && menuRef.current) {
      const focusedButton = menuRef.current.children[
        focusedIndex
      ] as HTMLElement;
      if (focusedButton) {
        focusedButton.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      }
    }
  }, [focusedIndex, isOpen]);

  const handleSelect = (optionValue: string) => {
    const { startDate, endDate } = getDateRange(optionValue);

    onChange({
      days: optionValue,
      startDate,
      endDate,
    });

    setIsOpen(false);
    buttonRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen) {
      // Open dropdown on Enter, Space, or Arrow keys
      if (
        event.key === "Enter" ||
        event.key === " " ||
        event.key === "ArrowDown" ||
        event.key === "ArrowUp"
      ) {
        event.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setFocusedIndex((prev) =>
          prev < options.length - 1 ? prev + 1 : prev
        );
        break;

      case "ArrowUp":
        event.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;

      case "Home":
        event.preventDefault();
        setFocusedIndex(0);
        break;

      case "End":
        event.preventDefault();
        setFocusedIndex(options.length - 1);
        break;

      case "Enter":
      case " ":
        event.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < options.length) {
          handleSelect(options[focusedIndex].value);
        }
        break;

      case "Escape":
        event.preventDefault();
        setIsOpen(false);
        buttonRef.current?.focus();
        break;

      case "Tab":
        setIsOpen(false);
        break;

      default:
        break;
    }
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <style>
        {`
          /* WebKit browsers */
          .dropdown-scroll::-webkit-scrollbar {
            width: 4px;
            height: 4px;
          }

          .dropdown-scroll::-webkit-scrollbar-track {
            background: transparent;
          }

          .dropdown-scroll::-webkit-scrollbar-thumb {
            background: #BFBFBF; /* grey-400 */
            border-radius: 10px;
            transition: background 0.2s ease;
          }

          .dropdown-scroll::-webkit-scrollbar-thumb:hover {
            background: #999999; /* grey-500 */
          }

          /* Dark mode variants (when a parent has .dark) */
          .dark .dropdown-scroll::-webkit-scrollbar-thumb {
            background: #737373; /* grey-600 */
          }

          .dark .dropdown-scroll::-webkit-scrollbar-thumb:hover {
            background: #4D4D4D; /* grey-700 */
          }

          /* Firefox */
          .dropdown-scroll {
            scrollbar-width: thin;
            scrollbar-color: #BFBFBF transparent; /* thumb track */
          }

          .dark .dropdown-scroll {
            scrollbar-color: #737373 transparent;
          }
        `}
      </style>

      {/* Trigger */}
      <button
        ref={buttonRef}
        type='button'
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        className='w-full h-[40px] flex items-center justify-between gap-2 px-4 py-2 bg-white dark:bg-blue-900 
          border border-grey-300 dark:border-grey-600 rounded hover:bg-gray-50 dark:hover:bg-blue-800 transition-colors'>
        <div
          className='flex items-center flex-1 min-w-0'
          style={{ gap: "15px" }}>
          {icon && (
            <i
              className={`ri-${icon} text-grey-800 dark:text-grey-100 flex-shrink-0`}
              style={{
                width: "20px",
                height: "20px",
                fontSize: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 0,
              }}
            />
          )}

          <Typography
            variant='regular-14'
            className='text-grey-800 dark:text-grey-300 truncate'>
            {selectedLabel}
          </Typography>
        </div>

        <div
          className={`transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""
            }`}
          style={{
            width: "24px",
            height: "24px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
          <img
            src={ArrowLightMode}
            alt='arrow'
            className='block dark:hidden w-full h-full'
          />
          <img
            src={ArrowDarkMode}
            alt='arrow'
            className='hidden dark:block w-full h-full'
          />
        </div>
      </button>

      {/* Menu */}
      {isOpen && (
        <div
          ref={menuRef}
          role='listbox'
          className='dropdown-scroll absolute top-full left-0 right-0 mt-1 bg-white dark:bg-blue-800
            dark:border-grey-600 rounded shadow-lg z-50 max-h-[240px] overflow-y-auto'>
          {options.map((option, index) => (
            (() => {
              let optionBackgroundClass = "bg-white dark:bg-blue-900";
              if (focusedIndex === index || value === option.value) {
                optionBackgroundClass = "bg-gray-100 dark:bg-blue-800";
              }
              return (
                <button
              key={option.value}
              type='button'
              role='option'
              aria-selected={value === option.value}
              onClick={() => handleSelect(option.value)}
              onMouseEnter={() => setFocusedIndex(index)}
              className={`w-full text-left px-4 py-2.5 
  transition-colors rounded-none relative hover:bg-gray-100
  ${optionBackgroundClass}
`}>
              <span
                className='
                  absolute bottom-0 left-3 right-3
                  h-[1px] bg-grey-300 dark:bg-grey-600
                  last:hidden
                '
              />

              <Typography
                variant='medium-14'
                className={`truncate ${value === option.value
                  ? "text-gray-900 dark:text-grey-100 font-medium"
                  : "text-gray-800 dark:text-grey-300"
                  }`}>
                {option.label}
              </Typography>
                </button>
              );
            })()
          ))}
        </div>
      )}
    </div>
  );
};

export default DialogDropdown;
